import * as array from "d3-array";
import { scaleLinear } from "d3-scale";
import * as shape from "d3-shape";
import React, { PureComponent } from "react";
import Path from "./AnimatedPath";

export interface ContentInset {
  top: number;
  left: number;
  right: number;
  bottom: number;
}

export interface BaseChartProps {
  height: number;
  width: number;
  data: any;
  svg?: object;
  style?: any;
  animationduration?: number;
  curve: shape.CurveFactory | shape.CurveFactoryLineOnly;
  contentInset: ContentInset;
  numberOfTicks?: number;
  gridMin?: number;
  gridMax?: number;
  yMin?: any;
  yMax?: any;
  xMin?: any;
  xMax?: any;
  clampX?: boolean;
  clampY?: boolean;
  xScale(): any;
  yScale(): any;
  xAccessor(obj: any): any;
  yAccessor(obj: any): any;
}

export interface BaseChartState {
  width: number;
  height: number;
}

class BaseChart extends PureComponent<BaseChartProps, BaseChartState> {
  static defaultProps = {
    svg: {},
    width: 100,
    height: 100,
    curve: shape.curveLinear,
    contentInset: {},
    numberOfTicks: 10,
    xScale: scaleLinear,
    yScale: scaleLinear,
    xAccessor: ({ index }: { index: number }) => index,
    yAccessor: ({ item }: { item: any }) => item,
  };

  constructor(props: BaseChartProps) {
    super(props);
  }

  // No idea what's happening here
  // eslint-disable-next-line
  createPaths(_obj: any) {
    throw 'Extending "Chart" requires you to override "createPaths';
  }

  render() {
    const {
      height,
      width,
      data,
      xAccessor,
      yAccessor,
      yScale,
      xScale,
      style,
      animationduration,
      numberOfTicks,
      contentInset: { top = 0, bottom = 0, left = 0, right = 0 },
      yMax,
      yMin,
      xMax,
      xMin,
      svg,
      children,
    } = this.props;

    if (data.length === 0) {
      return <div style={style} />;
    }

    const mappedData = data.map((item: any, index: number) => ({
      y: yAccessor({ item, index }),
      x: xAccessor({ item, index }),
    }));

    const yValues = mappedData.map((item: any) => item.y);
    const xValues = mappedData.map((item: any) => item.x);

    const yDomain = array.extent([...yValues, yMin, yMax]);
    const xDomain = array.extent([...xValues, xMin, xMax]);

    //invert range to support svg coordinate system
    const y = yScale()
      .domain(yDomain)
      .range([height - bottom, top])
      .nice(numberOfTicks);

    const x = xScale()
      .domain(xDomain)
      .range([left, width - right]);

    const paths: any = this.createPaths({
      data: mappedData,
      x,
      y,
    });

    const ticks = y.ticks(numberOfTicks);

    const extraProps = {
      x,
      y,
      data,
      ticks,
      width,
      height,
      ...paths,
    };

    return (
      height > 0 &&
      width > 0 && (
        <svg style={{ height, width }}>
          {React.Children.map(children, (child: any) => {
            if (child && child.props.belowChart) {
              return React.cloneElement(child, extraProps);
            }
            return null;
          })}
          <Path fill={"none"} {...svg} d={paths.path} animationduration={animationduration} />
          {React.Children.map(children, (child: any) => {
            if (child && !child.props.belowChart) {
              return React.cloneElement(child, extraProps);
            }
            return null;
          })}
        </svg>
      )
    );
  }
}

export default BaseChart;
